import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router';
import { fetchActiveNegotiations, fetchNotifications, markNotificationRead, markSeenOutcomes } from '../../store/slices/negotiationsSlice';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { itemsAPI } from '../../api/itemsAPI';
import { negotiationsAPI } from '../../api/negotiationsAPI';
import { getItemPublicPath } from '../../utils/itemLinks';
import { formatItemPriceEgp } from '../../utils/formatPrice';
import {
  getNegotiationDisplayLabel,
  getNegotiationItemCode,
  getNegotiationItemImageUrl,
} from '../../utils/negotiationDisplay';

const NegotiationsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 2rem;
`;

const NegotiationsList = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const NegotiationCard = styled(Link)`
  background-color: var(--bg-primary);
  border-radius: 0.5rem;
  box-shadow: var(--shadow-sm);
  padding: 1.5rem;
  text-decoration: none;
  color: inherit;
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex;
  gap: 1rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
`;

const ItemImageContainer = styled.div`
  width: 100px;
  height: 100px;
  background-color: var(--bg-secondary);
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
`;

const ItemImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const NegotiationContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const NegotiationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const NegotiationTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const ItemCodeText = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-family: monospace;
  margin: 0.25rem 0 0;
`;

const StatusBadge = styled.span`
  padding: 0.375rem 0.75rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  background-color: ${props => 
    props.status === 'ACTIVE' ? '#e3f2fd' :
    props.status === 'COMPLETED' ? '#e8f5e9' :
    props.status === 'REJECTED' ? '#ffebee' : '#f5f5f5'};
  color: ${props => 
    props.status === 'ACTIVE' ? '#1976d2' :
    props.status === 'COMPLETED' ? '#388e3c' :
    props.status === 'REJECTED' ? '#d32f2f' : '#757575'};
`;

const NegotiationMeta = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-top: 0.5rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1rem;
`;

const NotificationsSection = styled.section`
  margin-bottom: 2.5rem;
`;

const NotificationCard = styled(Link)`
  display: flex;
  gap: 1rem;
  padding: 1.25rem;
  margin-bottom: 1rem;
  background-color: ${props => (props.$unread ? '#f0f7ff' : 'var(--bg-primary)')};
  border: 1px solid ${props => (props.$accepted ? '#c8e6c9' : props.$rejected ? '#ffcdd2' : 'var(--border-color, #e0e0e0)')};
  border-radius: 0.5rem;
  text-decoration: none;
  color: inherit;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: var(--shadow-sm);
  }
`;

const NotificationMessage = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.9375rem;
  color: var(--text-primary);
  line-height: 1.5;
`;

const NotificationMeta = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.8125rem;
  color: var(--text-secondary);
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: -1rem 0 2rem;
`;

const FilterChip = styled.button`
  border: 1px solid ${(props) => (props.$active ? 'var(--primary-color)' : 'var(--border-color)')};
  background-color: ${(props) => (props.$active ? 'var(--primary-color)' : 'var(--bg-primary)')};
  color: ${(props) => (props.$active ? '#fff' : 'var(--text-primary)')};
  border-radius: 999px;
  padding: 0.45rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s, border-color 0.15s;

  &:hover {
    border-color: var(--primary-color);
  }
`;

const OUTCOME_NOTIFICATION_TYPES = new Set(['OFFER_ACCEPTED', 'OFFER_REJECTED']);

const INBOX_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'flea', label: 'Flea Messages' },
  { id: 'selling', label: 'Selling' },
  { id: 'buying', label: 'Buying' },
];

const EMPTY_COPY = {
  all: {
    title: 'No active negotiations',
    body: 'Start negotiating by submitting a price offer in EGP on any item page.',
  },
  flea: {
    title: 'Nothing from Flea yet',
    body: 'Updates about your offers and messages will show up here.',
  },
  selling: {
    title: 'No offers on your listings',
    body: 'When a buyer makes an offer on something you sell, it lands here.',
  },
  buying: {
    title: 'You have no offers out',
    body: 'Submit a price offer in EGP on any item page to start negotiating.',
  },
};

function getNotificationBadge(type) {
  switch (type) {
    case 'OFFER_ACCEPTED':
      return { label: 'Accepted', status: 'COMPLETED', glyph: '✅' };
    case 'OFFER_REJECTED':
      return { label: 'Rejected', status: 'REJECTED', glyph: '❌' };
    case 'NEW_OFFER':
      return { label: 'New offer', status: 'ACTIVE', glyph: '💰' };
    case 'NEW_MESSAGE':
      return { label: 'Message', status: 'ACTIVE', glyph: '✉️' };
    default:
      return { label: 'Update', status: 'ACTIVE', glyph: '✉️' };
  }
}

const NegotiationsPage = () => {
  const dispatch = useDispatch();
  const { activeNegotiations, notifications, loading, error } = useSelector((state) => state.negotiations);
  const { user } = useSelector((state) => state.auth);
  const [itemDetails, setItemDetails] = useState({});
  const [loadingItems, setLoadingItems] = useState({});
  const [activeFilter, setActiveFilter] = useState('all');

  const outcomeNotifications = (notifications || []).filter((n) =>
    OUTCOME_NOTIFICATION_TYPES.has(n.notification_type)
  );

  // The All tab keeps showing just the offer outcomes; the Messages tab opens
  // that up to everything Flea has sent. Selling and Buying are the negotiation
  // threads split by the side the user is on.
  const visibleNotifications =
    activeFilter === 'flea'
      ? notifications || []
      : activeFilter === 'all'
        ? outcomeNotifications
        : [];

  const visibleNegotiations = (activeNegotiations || []).filter((negotiation) => {
    if (activeFilter === 'flea') return false;
    if (activeFilter === 'selling') return negotiation.seller_id === user?.id;
    if (activeFilter === 'buying') return negotiation.buyer_id === user?.id;
    return true;
  });

  useEffect(() => {
    if (user) {
      dispatch(fetchActiveNegotiations());
      dispatch(fetchNotifications()).then(() => {
        // Opening the inbox counts as seeing completed outcomes — clear badge.
        dispatch(markSeenOutcomes());
      });
    }
  }, [dispatch, user]);

  // Fetch item details for each negotiation
  useEffect(() => {
    if (activeNegotiations.length > 0) {
      activeNegotiations.forEach((negotiation) => {
        if (negotiation.item_id && !itemDetails[negotiation.item_id] && !loadingItems[negotiation.item_id]) {
          setLoadingItems(prev => ({ ...prev, [negotiation.item_id]: true }));
          itemsAPI.getItemById(negotiation.item_id)
            .then((item) => {
              setItemDetails(prev => ({ ...prev, [negotiation.item_id]: item }));
            })
            .catch((err) => {
              console.error(`Failed to fetch item ${negotiation.item_id}:`, err);
            })
            .finally(() => {
              setLoadingItems(prev => ({ ...prev, [negotiation.item_id]: false }));
            });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeNegotiations]);

  useEffect(() => {
    if (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to load negotiations');
    }
  }, [error]);

  if (loading && activeNegotiations.length === 0 && outcomeNotifications.length === 0) {
    return <LoadingSpinner text="Loading negotiations..." />;
  }

  if (!user) {
    return (
      <NegotiationsContainer>
        <EmptyState>
          <h2>Please log in to view your negotiations</h2>
        </EmptyState>
      </NegotiationsContainer>
    );
  }

  return (
    <NegotiationsContainer>
      <PageTitle>My Negotiations</PageTitle>

      <FilterRow role="tablist" aria-label="Filter messages">
        {INBOX_FILTERS.map((filter) => (
          <FilterChip
            key={filter.id}
            type="button"
            role="tab"
            aria-selected={activeFilter === filter.id}
            $active={activeFilter === filter.id}
            onClick={() => setActiveFilter(filter.id)}
          >
            {filter.label}
          </FilterChip>
        ))}
      </FilterRow>

      {visibleNotifications.length > 0 && (
        <NotificationsSection>
          <SectionTitle>From Flea</SectionTitle>
          {visibleNotifications.map((notification) => {
            const isAccepted = notification.notification_type === 'OFFER_ACCEPTED';
            const isRejected = notification.notification_type === 'OFFER_REJECTED';
            const badge = getNotificationBadge(notification.notification_type);
            const offerAmount = notification.offer_amount_display ?? notification.offer_amount;
            const imageUrl = notification.item_image_url;
            const title = notification.item_title || 'Item';
            const code = notification.item_code;

            return (
              <NotificationCard
                key={notification.id}
                to={
                  isAccepted && String(notification.message || '').toLowerCase().includes('cart')
                    ? '/cart'
                    : `/negotiations/${negotiationsAPI.buildNegotiationRef(notification.negotiation)}`
                }
                $unread={!notification.is_read}
                $accepted={isAccepted}
                $rejected={isRejected}
                onClick={() => {
                  if (!notification.is_read) {
                    dispatch(markNotificationRead(notification.id));
                  }
                }}
              >
                <ItemImageContainer>
                  {imageUrl ? (
                    <ItemImage src={imageUrl} alt={title} loading="lazy" />
                  ) : (
                    <span style={{ fontSize: '2rem' }}>{badge.glyph}</span>
                  )}
                </ItemImageContainer>
                <NegotiationContent>
                  <NegotiationHeader>
                    <div>
                      <NegotiationTitle>{title}</NegotiationTitle>
                      {code ? <ItemCodeText>{code}</ItemCodeText> : null}
                    </div>
                    <StatusBadge status={badge.status}>
                      {badge.label}
                    </StatusBadge>
                  </NegotiationHeader>
                  <NotificationMessage style={{ whiteSpace: 'pre-line' }}>{notification.message}</NotificationMessage>
                  {isAccepted && String(notification.message || '').toLowerCase().includes('cart') ? (
                    <div style={{ marginTop: '0.5rem', fontWeight: 600, color: 'var(--primary-color)' }}>
                      Go to cart & checkout →
                    </div>
                  ) : null}
                  {offerAmount != null && (
                    <NotificationMeta>
                      Your offer: {formatItemPriceEgp(offerAmount)}
                    </NotificationMeta>
                  )}
                  <NotificationMeta>
                    {new Date(notification.created_at).toLocaleString()}
                  </NotificationMeta>
                </NegotiationContent>
              </NotificationCard>
            );
          })}
        </NotificationsSection>
      )}
      
      {visibleNegotiations.length === 0 && visibleNotifications.length === 0 ? (
        <EmptyState>
          <h3>{EMPTY_COPY[activeFilter].title}</h3>
          <p>{EMPTY_COPY[activeFilter].body}</p>
        </EmptyState>
      ) : (
        <NegotiationsList>
          {visibleNegotiations.map((negotiation) => {
            const item = itemDetails[negotiation.item_id];
            const itemImageUrl = getNegotiationItemImageUrl(negotiation, item);
            const displayLabel = getNegotiationDisplayLabel(negotiation, item);
            const itemCode = getNegotiationItemCode(negotiation, item);
            
            return (
              <NegotiationCard key={negotiation.id} to={`/negotiations/${negotiationsAPI.buildNegotiationRef(negotiation.id)}`}>
                <ItemImageContainer>
                  {itemImageUrl ? (
                    <ItemImage src={itemImageUrl} alt={displayLabel} loading="lazy" />
                  ) : loadingItems[negotiation.item_id] ? (
                    <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>...</span>
                  ) : (
                    <span style={{ fontSize: '2rem' }}>📦</span>
                  )}
                </ItemImageContainer>
                
                <NegotiationContent>
                  <NegotiationHeader>
                    <div>
                      <NegotiationTitle>
                        {displayLabel}
                      </NegotiationTitle>
                      {itemCode ? <ItemCodeText>{itemCode}</ItemCodeText> : null}
                    </div>
                    <StatusBadge status={negotiation.status}>
                      {negotiation.status}
                    </StatusBadge>
                  </NegotiationHeader>
                  <NegotiationMeta>
                    <span>Started: {new Date(negotiation.created_at).toLocaleDateString()}</span>
                    {negotiation.offers && negotiation.offers.length > 0 && (
                      <span>
                        Latest Offer: {formatItemPriceEgp(negotiation.offers[0].points_amount)}
                      </span>
                    )}
                  </NegotiationMeta>
                  {item && (
                    <Link 
                      to={getItemPublicPath(item)}
                      state={{ itemId: negotiation.item_id }}
                      onClick={(e) => e.stopPropagation()}
                      style={{ fontSize: '0.875rem', color: 'var(--primary-color)', marginTop: '0.5rem' }}
                    >
                      View Item →
                    </Link>
                  )}
                </NegotiationContent>
              </NegotiationCard>
            );
          })}
        </NegotiationsList>
      )}
    </NegotiationsContainer>
  );
};

export default NegotiationsPage;
